/**
 * Cohort retention curves M0–M12 → monthly logo churn rates.
 */

function roundPct(n) {
  return Math.round(n * 10000) / 10000;
}

/**
 * Monthly churn rate for a cohort at a given age (1 = first month after signup).
 * @param {number[]} retention — cumulative retention M0..M12 (M0 = 1.0)
 * @param {number} ageMonth — 1-based month since cohort vintage
 */
export function monthlyChurnRate(retention, ageMonth) {
  if (!retention?.length) return 0;
  const idx = Math.min(Math.max(1, ageMonth), retention.length - 1);
  const prev = retention[idx - 1] ?? 1;
  const curr = retention[idx] ?? prev;
  if (prev <= 0) return 0;
  return roundPct(Math.max(0, Math.min(1, 1 - curr / prev)));
}

/**
 * Steady-state monthly churn from the last two retention points.
 * @param {number[]} retention
 */
export function effectiveMonthlyChurn(retention) {
  if (!retention || retention.length < 2) return 0;
  return monthlyChurnRate(retention, retention.length - 1);
}

/**
 * Derive per-age monthly churn rates from a retention curve.
 * @param {number[]} retention
 * @returns {number[]}
 */
export function deriveLogoChurnFromCurve(retention) {
  if (!retention?.length) return [];
  return retention.slice(1).map((_, i) => monthlyChurnRate(retention, i + 1));
}

/**
 * Apply cohort aging — returns churned logos and surviving logos per cohort.
 * @param {Array<{ vintageMonth: number, logos: number, arpu: number }>} cohorts
 * @param {number} monthIndex
 * @param {number[]} retention
 * @param {number} [churnDeltaPp] — one-time additive churn shock in percentage points
 */
export function ageCohorts(cohorts, monthIndex, retention, churnDeltaPp = 0) {
  let churnedLogos = 0;
  let churnedMrr = 0;
  const surviving = [];
  const shock = (Number(churnDeltaPp) || 0) / 100;

  for (const cohort of cohorts) {
    const age = monthIndex - cohort.vintageMonth;
    if (age <= 0) {
      surviving.push(cohort);
      continue;
    }
    const rate = Math.min(1, Math.max(0, monthlyChurnRate(retention, age) + shock));
    const churn = cohort.logos * rate;
    const remaining = Math.max(0, cohort.logos - churn);
    churnedLogos += churn;
    churnedMrr += churn * cohort.arpu;
    if (remaining > 0) {
      surviving.push({ ...cohort, logos: remaining });
    }
  }

  return {
    cohorts: surviving,
    churnedLogos: roundPct(churnedLogos),
    churnedMrr: roundMoney(churnedMrr),
  };
}

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Normalize retention array to length 13 (M0–M12), padding with last value.
 * @param {number[]} retention
 */
export function normalizeRetention(retention) {
  const base = Array.isArray(retention) ? [...retention] : [];
  if (base.length === 0) base.push(1);
  if (Number(base[0]) !== 1) base.unshift(1);
  while (base.length < 13) {
    base.push(base[base.length - 1]);
  }
  let prior = 1;
  return base.slice(0, 13).map((v, i) => {
    if (i === 0) return 1;
    const parsed = Number(v);
    const normalized = Number.isFinite(parsed)
      ? Math.min(1, Math.max(0, parsed))
      : prior;
    prior = Math.min(prior, normalized);
    return prior;
  });
}

/**
 * Scale each age-specific churn hazard and rebuild cumulative retention.
 * This keeps a churn multiplier semantically different from multiplying
 * cumulative attrition at every age.
 */
export function scaleRetentionChurn(retention, multiplier = 1) {
  const base = normalizeRetention(retention);
  const parsedMultiplier = Number(multiplier);
  const factor = Number.isFinite(parsedMultiplier) ? Math.max(0, parsedMultiplier) : 1;
  const adjusted = [1];
  for (let i = 1; i < base.length; i += 1) {
    const baseChurn =
      base[i - 1] > 0
        ? Math.max(0, Math.min(1, 1 - base[i] / base[i - 1]))
        : 0;
    const churn = Math.min(1, baseChurn * factor);
    adjusted.push(Math.max(0, adjusted[i - 1] * (1 - churn)));
  }
  return adjusted;
}

/**
 * Default retention curves by scenario key.
 */
export const DEFAULT_RETENTION = {
  bear: [1, 0.55, 0.42, 0.35, 0.32, 0.3, 0.28, 0.27, 0.26, 0.25, 0.24, 0.23, 0.22],
  base: [1, 0.7, 0.58, 0.52, 0.48, 0.45, 0.43, 0.41, 0.4, 0.39, 0.38, 0.37, 0.36],
  bull: [1, 0.8, 0.68, 0.6, 0.55, 0.52, 0.5, 0.48, 0.47, 0.46, 0.45, 0.44, 0.43],
};
